import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminCategories.css';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: '',
    image_url: ''
  });

  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCategories(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch categories');
        }
      } else {
        setError('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Error loading categories');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, or WEBP)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB');
      return;
    }

    setUploading(true);
    const formDataImg = new FormData();
    formDataImg.append('image', file);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/upload-category-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formDataImg
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setFormData(prev => ({ ...prev, image_url: data.data.image_url }));
        alert('Image uploaded successfully');
      } else {
        alert(data.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'name' && !prev.slug && { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }

    const submitData = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      parent_id: formData.parent_id === '' ? null : parseInt(formData.parent_id),
      image_url: formData.image_url
    };

    try {
      const url = editingCategory 
        ? `${API_BASE_URL}/admin/categories/${editingCategory.id}` 
        : `${API_BASE_URL}/admin/categories`;
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert(editingCategory ? 'Category updated successfully' : 'Category created successfully');
        fetchCategories();
        resetForm();
      } else {
        alert(data.message || 'Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    const category = categories.find(c => c.id === categoryId);
    if (category && category.product_count > 0) {
      if (!window.confirm(`Category "${categoryName}" has ${category.product_count} products. Deleting it will move products to uncategorized. Are you sure you want to delete?`)) {
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to delete category "${categoryName}"?`)) {
        return;
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Category deleted successfully');
        fetchCategories();
      } else {
        alert(data.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      parent_id: category.parent_id || '',
      image_url: category.image_url || ''
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      parent_id: '',
      image_url: ''
    });
    setEditingCategory(null);
    setShowAddForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="admin-categories-loading">
        <div className="spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-categories-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-categories">
      <div className="admin-categories-header">
        <h1>Categories Management</h1>
        
        <div className="header-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>}
          </div>
          <button className="add-btn" onClick={() => setShowAddForm(true)}>+ Add New Category</button>
          <button className="refresh-btn" onClick={fetchCategories}>⟳ Refresh</button>
        </div>
      </div>

      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-group">
                <label>Category Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="e.g., Eyeglasses" />
              </div>

              <div className="form-group">
                <label>Slug</label>
                <input type="text" name="slug" value={formData.slug} onChange={handleInputChange} placeholder="auto-generated from name" />
                <small>URL-friendly version of the name (e.g., eyeglasses)</small>
              </div>

              <div className="form-group">
                <label>Parent Category</label>
                <select name="parent_id" value={formData.parent_id} onChange={handleInputChange}>
                  <option value="">None (Top Level)</option>
                  {categories.filter(c => c.id !== (editingCategory?.id)).map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3" placeholder="Category description..." />
              </div>

              <div className="form-group">
                <label>Category Image</label>
                <div className="image-upload-container">
                  {formData.image_url && (
                    <div className="image-preview">
                      <img src={formData.image_url} alt="Category preview" />
                      <button type="button" className="remove-image" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}>×</button>
                    </div>
                  )}
                  <div className="upload-area">
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageUpload} style={{ display: 'none' }} />
                    <button type="button" className="upload-btn" onClick={triggerFileUpload} disabled={uploading}>
                      {uploading ? 'Uploading...' : 'Choose Image'}
                    </button>
                    <small>Max size: 2MB. Allowed: JPEG, PNG, WEBP</small>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">{editingCategory ? 'Update Category' : 'Create Category'}</button>
                <button type="button" className="cancel-btn" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="categories-table-container">
        {filteredCategories.length === 0 ? (
          <div className="no-data">{searchTerm ? 'No categories match your search.' : 'No categories found. Click "Add New Category" to create one.'}</div>
        ) : (
          <table className="categories-table">
            <thead>
              <tr><th>ID</th><th>Name</th><th>Slug</th><th>Products</th><th>Parent</th><th>Image</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filteredCategories.map(category => (
                <tr key={category.id}>
                  <td className="id-cell">{category.id}</td>
                  <td className="name-cell">
                    <div className="category-name">{category.name}</div>
                    {category.description && <div className="category-description">{category.description.substring(0, 60)}...</div>}
                  </td>
                  <td className="slug-cell">{category.slug}</td>
                  <td className="product-count-cell"><span className={`product-badge ${category.product_count > 0 ? 'has-products' : 'no-products'}`}>{category.product_count || 0}</span></td>
                  <td className="parent-cell">{category.parent_name || '-'}</td>
                  <td className="image-cell">
                    {category.image_url ? (
                      <img src={category.image_url} alt={category.name} className="category-table-thumb" />
                    ) : (
                      <span className="no-image">No image</span>
                    )}
                  </td>
                  <td className="date-cell">{formatDate(category.created_at)}</td>
                  <td className="actions-cell">
                    <button className="edit-btn" onClick={() => handleEditCategory(category)} title="Edit Category">✏️</button>
                    <button className="delete-btn" onClick={() => handleDeleteCategory(category.id, category.name)} title="Delete Category">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="categories-footer">
        <div className="categories-count">Showing {filteredCategories.length} of {categories.length} categories</div>
      </div>
    </div>
  );
};

export default AdminCategories;